import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Heart, Download } from "lucide-react";
import { toast } from "sonner";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { clientForPortalUser, portalDataForClient } from "@/mocks/saas-data";

export const Route = createFileRoute("/portal/galleries")({
  ssr: false,
  head: () => ({ meta: [{ title: "Portal — Galleries" }] }),
  component: Page,
});

function Page() {
  const user = useRequireRole(["client_portal"]);
  const [favorites, setFavorites] = useState<Record<string, boolean>>({});
  if (!user) return null;
  const client = clientForPortalUser(user.id);
  const galleries = client ? portalDataForClient(client.name).galleries : [];

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Your galleries" description="Proof, select favorites, download deliveries." />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {galleries.map((g) => (
          <div key={g.id} className="rounded-xl bg-card ring-1 ring-border overflow-hidden">
            <div
              className="aspect-[4/3] w-full"
              style={{ background: `linear-gradient(135deg, oklch(0.6 0.08 ${g.coverHue}) 0%, oklch(0.3 0.04 ${g.coverHue}) 100%)` }}
            />
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="truncate font-medium">{g.title}</h3>
                <StatusBadge status={g.status} />
              </div>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {g.imageCount} images · shot {g.shootDate}
              </p>
              <div className="mt-4 grid grid-cols-6 gap-1.5">
                {Array.from({ length: 6 }).map((_, i) => {
                  const key = `${g.id}_${i}`;
                  const fav = !!favorites[key];
                  return (
                    <button
                      key={key}
                      onClick={() => setFavorites((p) => ({ ...p, [key]: !p[key] }))}
                      className="group relative aspect-square overflow-hidden rounded ring-1 ring-border"
                      style={{ background: `oklch(${0.5 + i * 0.06} 0.08 ${g.coverHue + i * 10})` }}
                    >
                      <Heart
                        className={`absolute inset-0 m-auto size-3.5 transition ${fav ? "fill-red-500 text-red-500" : "text-white/0 group-hover:text-white/80"}`}
                      />
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => toast.success(`Downloading ${g.title}`)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-3 py-2 text-xs font-medium text-background hover:opacity-90 disabled:opacity-50"
                disabled={g.status === "uploading"}
              >
                <Download className="size-3.5" /> {g.status === "uploading" ? "Uploading…" : "Download"}
              </button>
            </div>
          </div>
        ))}
        {galleries.length === 0 && (
          <p className="col-span-full rounded-xl bg-card p-10 text-center text-sm text-muted-foreground ring-1 ring-border">
            No galleries yet.
          </p>
        )}
      </div>
    </div>
  );
}
